import React, { useMemo } from "react";
import { Link, useLocation } from "@tanstack/react-router";
import { PanelLeft } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import BrandMark from "./BrandMark";
import UserMenu from "./UserMenu";
import { groupNavItems, isNavItemActive, sectionLabel, type SidebarItem } from "./nav";

export type { SidebarItem } from "./nav";

interface SidebarProps {
  items: SidebarItem[];
  /** Portal name, e.g. "Student Portal". */
  title?: string;
  /** Mobile drawer state. */
  open: boolean;
  onClose: () => void;
  collapsed?: boolean;
  onToggle?: () => void;
  isMobile?: boolean;
  /** @deprecated Width is fixed by the shell; kept for compatibility. */
  width?: number;
}

function NavBadge({ badge }: { badge: NonNullable<SidebarItem["badge"]> }) {
  const isCount = /^\d+$/.test(badge.label);
  return (
    <span
      className={cn(
        "ml-auto flex h-5 shrink-0 items-center justify-center rounded-full text-[11px] leading-none font-medium tabular-nums",
        isCount
          ? "min-w-5 bg-primary px-1.5 text-primary-foreground"
          : "bg-pastel-sky px-2 text-pastel-sky-ink",
      )}
    >
      {isCount ? String(Number(badge.label)) : badge.label}
    </span>
  );
}

function NavLink({
  item,
  active,
  collapsed,
  onNavigate,
}: {
  item: SidebarItem;
  active: boolean;
  collapsed: boolean;
  onNavigate?: () => void;
}) {
  const icon = React.isValidElement(item.icon)
    ? React.cloneElement(item.icon as React.ReactElement<any>, { size: 18, strokeWidth: 1.6 })
    : item.icon;

  const link = (
    <Link
      to={item.path as any}
      onClick={onNavigate}
      aria-current={active ? "page" : undefined}
      className={cn(
        "group relative flex h-10 items-center gap-3 rounded-full px-3.5 text-sm transition-[background-color,color,box-shadow]",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring",
        active
          ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground shadow-soft"
          : "text-sidebar-foreground hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground",
        collapsed && "mx-auto size-10 justify-center px-0",
      )}
    >
      <span
        className={cn(
          "flex shrink-0 items-center [&_svg]:size-[18px]",
          active ? "text-sidebar-accent-foreground" : "text-body group-hover:text-foreground",
        )}
      >
        {icon}
      </span>
      {!collapsed && <span className="truncate">{item.label}</span>}
      {!collapsed && item.badge && <NavBadge badge={item.badge} />}
      {collapsed && item.badge && (
        <span className="absolute top-1.5 right-1.5 size-2 rounded-full bg-primary ring-2 ring-sidebar" aria-hidden />
      )}
    </Link>
  );

  if (!collapsed) return link;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side="right" sideOffset={10}>
        {item.label}
        {item.badge ? ` · ${item.badge.label}` : ""}
      </TooltipContent>
    </Tooltip>
  );
}

function SidebarBody({
  items,
  collapsed,
  onToggle,
  onNavigate,
  showToggle,
}: {
  items: SidebarItem[];
  collapsed: boolean;
  onToggle?: () => void;
  onNavigate?: () => void;
  showToggle: boolean;
}) {
  const { pathname } = useLocation();
  const groups = useMemo(() => Object.entries(groupNavItems(items)), [items]);

  // Highlight only the most specific match so "/x" and "/x/y" are never both active.
  const activePath = useMemo(() => {
    const matches = items.filter((item) => isNavItemActive(pathname, item.path));
    return matches.sort((a, b) => b.path.length - a.path.length)[0]?.path;
  }, [items, pathname]);

  return (
    <div className="flex h-full min-h-0 flex-col bg-sidebar bg-[radial-gradient(120%_60%_at_0%_0%,color-mix(in_oklab,var(--sidebar-accent)_70%,transparent),transparent_70%)] text-sidebar-foreground">
      <div
        className={cn(
          "flex h-[72px] shrink-0 items-center gap-2",
          collapsed ? "justify-center px-3" : "justify-between pr-4 pl-6",
        )}
      >
        {collapsed ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={onToggle}
                className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring"
                aria-label="Expand sidebar"
              >
                <BrandMark showWordmark={false} />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right">Expand sidebar</TooltipContent>
          </Tooltip>
        ) : (
          <>
            <Link
              to="/"
              className="min-w-0 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring"
            >
              <BrandMark />
            </Link>
            {showToggle && (
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={onToggle}
                aria-label="Collapse sidebar"
                className="text-body hover:bg-sidebar-accent/70 hover:text-foreground"
              >
                <PanelLeft />
              </Button>
            )}
          </>
        )}
      </div>

      <nav
        aria-label="Main"
        className={cn(
          "min-h-0 flex-1 overflow-y-auto overflow-x-hidden pt-2 pb-6 [mask-image:linear-gradient(to_bottom,black_calc(100%-28px),transparent)]",
          collapsed ? "px-3" : "px-4",
        )}
      >
        {groups.map(([section, sectionItems], index) => (
          <div key={section} className={cn(index > 0 && "mt-6")}>
            {collapsed ? (
              index > 0 && <div className="mx-3 mb-4 h-px bg-sidebar-border" aria-hidden />
            ) : (
              <p className="mb-2 px-2 text-[13px] text-mute">{sectionLabel(section)}</p>
            )}
            <ul className="space-y-1">
              {sectionItems.map((item) => (
                <li key={item.path}>
                  <NavLink
                    item={item}
                    active={item.path === activePath}
                    collapsed={collapsed}
                    onNavigate={onNavigate}
                  />
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      <div className={cn("shrink-0 pb-4", collapsed ? "px-3" : "px-4")}>
        {!collapsed && !groups.some(([section]) => section.trim().toLowerCase() === "account") && (
          <p className="mb-2 px-2 text-[13px] text-mute">Account</p>
        )}
        <UserMenu variant="sidebar" collapsed={collapsed} />
        {collapsed && showToggle && (
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={onToggle}
            aria-label="Expand sidebar"
            className="mx-auto mt-2 flex text-body hover:bg-sidebar-accent/70 hover:text-foreground"
          >
            <PanelLeft />
          </Button>
        )}
      </div>
    </div>
  );
}

export default function Sidebar({
  items,
  title,
  open,
  onClose,
  collapsed = false,
  onToggle,
  isMobile = false,
}: SidebarProps) {
  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={(next) => !next && onClose()}>
        <SheetContent
          side="left"
          className="w-[min(296px,86vw)] gap-0 overflow-hidden border-r-0 p-0 sm:max-w-none [&>button:first-of-type]:top-4"
        >
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <SheetDescription className="sr-only">Move between sections of {title || "the app"}</SheetDescription>
          <SidebarBody items={items} collapsed={false} onNavigate={onClose} showToggle={false} />
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <aside
      className={cn(
        "sticky top-0 h-[calc(100dvh-var(--banner-h,0px))] shrink-0 overflow-hidden transition-[width] duration-200 ease-out",
        collapsed ? "w-[76px]" : "w-[268px]",
      )}
    >
      <SidebarBody items={items} collapsed={collapsed} onToggle={onToggle} showToggle />
    </aside>
  );
}
